import type { CommonNavContent } from 'client-site-chrome-ui/content/types/commonNavigation';
import type { DrilldownNavItem } from 'client-site-chrome-ui/types/drilldownNavItem';

const DEFAULT_TITLES = {
  1: 'no display text for level 1',
  2: 'no display text for level 2',
  3: 'no display text for level 3',
} as const;

/**
 * Parses navigation data and returns title and URL with fallbacks
 */
const parseNavigationData = (
  displayText: string | undefined,
  navigationLink: Array<{ title?: string; url?: string }> | undefined,
  level: keyof typeof DEFAULT_TITLES,
): { title: string; url: string } => {
  const title = displayText || navigationLink?.[0]?.title || DEFAULT_TITLES[level];
  const url = navigationLink?.[0]?.url || '';
  return { title, url };
};

const createNavItem = (
  title: string,
  url: string,
  items: DrilldownNavItem[],
  level: number,
): DrilldownNavItem => ({
  title,
  url,
  items,
  level,
  ...(url && { role: 'regular_link' as const }),
});

const addParentLinkToChildren = (
  item: DrilldownNavItem,
  childLevel: number,
): DrilldownNavItem[] => {
  if (item.url && item.items && item.items.length > 0) {
    return [
      ...item.items,
      {
        title: `See all ${item.title}`,
        url: item.url,
        items: [],
        level: childLevel,
        role: 'see_all_link' as const,
      },
    ];
  }
  return item.items || [];
};

export const transformToDrilldownProp = (content: CommonNavContent): DrilldownNavItem => {
  const navigation = {
    title: 'Shop by department',
    items: [
      ...content.l1_navigation.map((l1) => {
        const { title: l1Title, url: l1Url } = parseNavigationData(
          l1.l1_display_text,
          l1.l1_navigation_link,
          1,
        );

        const l2Items = l1.l2_navigation.map((l2) => {
          const { title: l2Title, url: l2Url } = parseNavigationData(
            l2.l2_display_text,
            l2.l2_navigation_link,
            2,
          );

          const l3Items = l2.l3_navigation.map((l3) => {
            const { title: l3Title, url: l3Url } = parseNavigationData(
              l3.l3_display_text,
              l3.l3_navigation_link,
              3,
            );

            return createNavItem(l3Title, l3Url, [], 3);
          });

          const l2Item = createNavItem(l2Title, l2Url, l3Items, 2);
          const l3ItemsWithSeeAll = addParentLinkToChildren(l2Item, 3);

          return {
            ...l2Item,
            items: l3ItemsWithSeeAll,
          };
        });

        const l1Item = createNavItem(l1Title, l1Url, l2Items, 1);
        const l2ItemsWithSeeAll = addParentLinkToChildren(l1Item, 2);

        return {
          ...l1Item,
          items: l2ItemsWithSeeAll,
        };
      }),
    ],
    level: 0,
  };

  return navigation;
};
